import * as tslib_1 from "tslib";
import { Attr, Value, Text, Form, StringInput, Iteratee, StringOutput, Recon } from '@swim/recon';
var PI = Math.PI;
var TAU = 2 * PI;
var Angle = (function () {
    function Angle() {
    }
    Angle.prototype.isDefined = function () {
        return this.value !== 0;
    };
    Angle.prototype.plus = function (that, units) {
        if (units === void 0) { units = this.units; }
        return Angle.fromAny(this.toValue(units) + Angle.fromAny(that).toValue(units), units);
    };
    Angle.prototype.neg = function (units) {
        if (units === void 0) { units = this.units; }
        return Angle.fromAny(-this.toValue(units), units);
    };
    Angle.prototype.minus = function (that, units) {
        if (units === void 0) { units = this.units; }
        return Angle.fromAny(this.toValue(units) - Angle.fromAny(that).toValue(units), units);
    };
    Angle.prototype.times = function (scalar, units) {
        if (units === void 0) { units = this.units; }
        return Angle.fromAny(this.toValue(units) * scalar, units);
    };
    Angle.prototype.divide = function (scalar, units) {
        if (units === void 0) { units = this.units; }
        return Angle.fromAny(this.toValue(units) / scalar, units);
    };
    Angle.prototype.norm = function (total, units) {
        if (units === void 0) { units = this.units; }
        return Angle.fromAny(this.toValue(units) / Angle.fromAny(total).toValue(units), units);
    };
    Angle.prototype.deg = function () {
        return new DegAngle(this.degValue());
    };
    Angle.prototype.grad = function () {
        return new GradAngle(this.gradValue());
    };
    Angle.prototype.rad = function () {
        return new RadAngle(this.radValue());
    };
    Angle.prototype.turn = function () {
        return new TurnAngle(this.turnValue());
    };
    Angle.prototype.toValue = function (units) {
        switch (units) {
            case 'deg': return this.degValue();
            case 'grad': return this.gradValue();
            case 'rad': return this.radValue();
            case 'turn': return this.turnValue();
            default: throw new TypeError(units);
        }
    };
    Angle.prototype.to = function (units) {
        switch (units) {
            case 'deg': return this.deg();
            case 'grad': return this.grad();
            case 'rad': return this.rad();
            case 'turn': return this.turn();
            default: throw new TypeError(units);
        }
    };
    Angle.prototype.equals = function (that) {
        if (that instanceof Angle) {
            return this.value === that.value && this.units === that.units;
        }
        return false;
    };
    Angle.prototype.toString = function () {
        return this.value + this.units;
    };
    Angle.form = function (units) {
        if (units !== undefined) {
            return new AngleForm(units);
        }
        else {
            return Angle.Form;
        }
    };
    Angle.zero = function (units) {
        units = units || 'rad';
        switch (units) {
            case 'deg': return new DegAngle(0);
            case 'grad': return new GradAngle(0);
            case 'rad': return new RadAngle(0);
            case 'turn': return new TurnAngle(0);
            default: throw new TypeError(units);
        }
    };
    Angle.deg = function (value) {
        return new DegAngle(value);
    };
    Angle.grad = function (value) {
        return new GradAngle(value);
    };
    Angle.rad = function (value) {
        return new RadAngle(value);
    };
    Angle.turn = function (value) {
        return new TurnAngle(value);
    };
    Angle.fromAny = function (value, units) {
        if (value instanceof Angle) {
            return value;
        }
        else if (typeof value === 'number') {
            if (typeof units === 'string') {
                switch (units) {
                    case 'deg': return new DegAngle(value);
                    case 'grad': return new GradAngle(value);
                    case '':
                    case 'rad': return new RadAngle(value);
                    case 'turn': return new TurnAngle(value);
                    default: throw new Error(units);
                }
            }
            else {
                return new RadAngle(value);
            }
        }
        else if (typeof value === 'string') {
            return Angle.parse(value);
        }
        else {
            throw new Error();
        }
    };
    Angle.fromValue = function (value) {
        if (value.length === 2) {
            var num = value.getItem(0).numberValue();
            var units = value.getItem(1);
            if (num !== undefined && isFinite(num) && units instanceof Attr && units.value === Value.Extant) {
                switch (units.key.value) {
                    case 'deg': return new DegAngle(num);
                    case 'grad': return new GradAngle(num);
                    case 'rad': return new RadAngle(num);
                    case 'turn': return new TurnAngle(num);
                    default:
                }
            }
        }
        return undefined;
    };
    Angle.parse = function (str, defaultUnits) {
        var input = new StringInput(str);
        while (!input.isEmpty() && Iteratee.isSpace(input.head())) {
            input.step();
        }
        var output = AngleParser.parse(input, defaultUnits);
        if (output.isDone()) {
            while (!input.isEmpty() && Iteratee.isSpace(input.head())) {
                input.step();
            }
            if (!input.isEmpty()) {
                output = Iteratee.unexpected(input);
            }
        }
        return output.bind();
    };
    Angle.Form = undefined;
    return Angle;
}());
export { Angle };
var DegAngle = (function (_super) {
    tslib_1.__extends(DegAngle, _super);
    function DegAngle(value) {
        var _this = _super.call(this) || this;
        _this.value = value;
        return _this;
    }
    Object.defineProperty(DegAngle.prototype, "units", {
        get: function () {
            return 'deg';
        },
        enumerable: true,
        configurable: true
    });
    DegAngle.prototype.degValue = function () {
        return this.value;
    };
    DegAngle.prototype.gradValue = function () {
        return this.value * 10 / 9;
    };
    DegAngle.prototype.radValue = function () {
        return this.value * PI / 180;
    };
    DegAngle.prototype.turnValue = function () {
        return this.value / 360;
    };
    DegAngle.prototype.deg = function () {
        return this;
    };
    return DegAngle;
}(Angle));
export { DegAngle };
var GradAngle = (function (_super) {
    tslib_1.__extends(GradAngle, _super);
    function GradAngle(value) {
        var _this = _super.call(this) || this;
        _this.value = value;
        return _this;
    }
    Object.defineProperty(GradAngle.prototype, "units", {
        get: function () {
            return 'grad';
        },
        enumerable: true,
        configurable: true
    });
    GradAngle.prototype.degValue = function () {
        return this.value * 0.9;
    };
    GradAngle.prototype.gradValue = function () {
        return this.value;
    };
    GradAngle.prototype.radValue = function () {
        return this.value * PI / 200;
    };
    GradAngle.prototype.turnValue = function () {
        return this.value / 400;
    };
    GradAngle.prototype.grad = function () {
        return this;
    };
    return GradAngle;
}(Angle));
export { GradAngle };
var RadAngle = (function (_super) {
    tslib_1.__extends(RadAngle, _super);
    function RadAngle(value) {
        var _this = _super.call(this) || this;
        _this.value = value;
        return _this;
    }
    Object.defineProperty(RadAngle.prototype, "units", {
        get: function () {
            return 'rad';
        },
        enumerable: true,
        configurable: true
    });
    RadAngle.prototype.degValue = function () {
        return this.value * 180 / PI;
    };
    RadAngle.prototype.gradValue = function () {
        return this.value * 200 / PI;
    };
    RadAngle.prototype.radValue = function () {
        return this.value;
    };
    RadAngle.prototype.turnValue = function () {
        return this.value / TAU;
    };
    RadAngle.prototype.rad = function () {
        return this;
    };
    return RadAngle;
}(Angle));
export { RadAngle };
var TurnAngle = (function (_super) {
    tslib_1.__extends(TurnAngle, _super);
    function TurnAngle(value) {
        var _this = _super.call(this) || this;
        _this.value = value;
        return _this;
    }
    Object.defineProperty(TurnAngle.prototype, "units", {
        get: function () {
            return 'turn';
        },
        enumerable: true,
        configurable: true
    });
    TurnAngle.prototype.degValue = function () {
        return this.value * 360;
    };
    TurnAngle.prototype.gradValue = function () {
        return this.value * 400;
    };
    TurnAngle.prototype.radValue = function () {
        return this.value * TAU;
    };
    TurnAngle.prototype.turnValue = function () {
        return this.value;
    };
    TurnAngle.prototype.turn = function () {
        return this;
    };
    return TurnAngle;
}(Angle));
export { TurnAngle };
var AngleParser = (function (_super) {
    tslib_1.__extends(AngleParser, _super);
    function AngleParser(defaultUnits, valueParser, unitsOutput, step) {
        var _this = _super.call(this) || this;
        _this.defaultUnits = defaultUnits;
        _this.valueParser = valueParser;
        _this.unitsOutput = unitsOutput;
        _this.step = step;
        return _this;
    }
    AngleParser.prototype.feed = function (input) {
        return AngleParser.parse(input, this.defaultUnits, this.valueParser, this.unitsOutput, this.step);
    };
    AngleParser.parse = function (input, defaultUnits, valueParser, unitsOutput, step) {
        if (defaultUnits === void 0) { defaultUnits = ''; }
        if (step === void 0) { step = 1; }
        var c = 0;
        if (step === 1) {
            if (!valueParser) {
                valueParser = Recon.Factory.parseDecimal(input);
            }
            while (valueParser.isCont() && (!input.isEmpty() || input.isDone())) {
                valueParser = valueParser.feed(input);
            }
            if (valueParser.isDone()) {
                step = 2;
            }
            else if (valueParser.isError()) {
                return valueParser.asError();
            }
        }
        if (step === 2) {
            unitsOutput = unitsOutput || new StringOutput();
            while (!input.isEmpty() && (c = input.head(), Iteratee.isAlpha(c))) {
                input.step();
                unitsOutput.push(c);
            }
            if (!input.isEmpty() || input.isDone()) {
                var value = valueParser.bind().numberValue();
                var units = unitsOutput.bind() || defaultUnits;
                switch (units) {
                    case 'deg': return Iteratee.done(new DegAngle(value));
                    case 'grad': return Iteratee.done(new GradAngle(value));
                    case '':
                    case 'rad': return Iteratee.done(new RadAngle(value));
                    case 'turn': return Iteratee.done(new TurnAngle(value));
                    default: return Iteratee.error(units);
                }
            }
        }
        return new AngleParser(defaultUnits, valueParser, unitsOutput, step);
    };
    return AngleParser;
}(Iteratee));
export { AngleParser };
var AngleForm = (function (_super) {
    tslib_1.__extends(AngleForm, _super);
    function AngleForm(units) {
        var _this = _super.call(this) || this;
        _this.units = units;
        return _this;
    }
    Object.defineProperty(AngleForm.prototype, "unit", {
        get: function () {
            return Angle.zero(this.units);
        },
        enumerable: true,
        configurable: true
    });
    AngleForm.prototype.mold = function (angle) {
        angle = Angle.fromAny(angle);
        return Text.valueOf(angle.toString());
    };
    AngleForm.prototype.cast = function (value) {
        var angle;
        try {
            angle = Angle.fromValue(value);
            if (!angle) {
                var str = value.stringValue();
                if (str !== undefined) {
                    angle = Angle.parse(str);
                }
            }
        }
        catch (e) {
        }
        return angle;
    };
    return AngleForm;
}(Form));
Angle.Form = new AngleForm();
//# sourceMappingURL=data:application/json;base64,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